import { authenticatedAPI } from '../utils/authApi';

class ChallengesApiService {
  constructor() {
    this.apiName = 'challenges'; // Use the challenges API configured in Amplify
  }

  async getChallenges(options = {}) {
    try {
      const { 
        status, 
        challenge_type, 
        my_challenges = false,
        limit = 20 
      } = options;

      const queryParams = {};
      if (status) queryParams.status = status;
      if (challenge_type) queryParams.challenge_type = challenge_type;
      if (my_challenges) queryParams.my_challenges = 'true';
      if (limit) queryParams.limit = limit.toString();

      const response = await authenticatedAPI.get(this.apiName, '/challenges', {
        queryStringParameters: queryParams
      });
      
      // Handle different response formats
      return response.challenges || response.data || response;
    } catch (error) {
      console.error('Error fetching challenges:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async getChallengeDetails(challengeId) {
    try {
      const response = await authenticatedAPI.get(this.apiName, `/challenges/${challengeId}`);
      return response;
    } catch (error) {
      console.error('Error fetching challenge details:', error);
      throw error;
    }
  }

  async joinChallenge(challengeId, teamId = null) {
    try {
      const body = teamId ? { team_id: teamId } : {};
      
      // Use challenge-specific path - this will be handled by the Lambda function
      const response = await authenticatedAPI.post(this.apiName, `/challenges/${challengeId}/join`, {
        body: body
      });
      
      return response;
    } catch (error) {
      console.error('Error joining challenge:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async leaveChallenge(challengeId) {
    try {
      const response = await authenticatedAPI.post(this.apiName, `/challenges/${challengeId}/leave`, {
        body: {}
      });
      
      return response;
    } catch (error) {
      console.error('Error leaving challenge:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async getChallengeAnalytics(challengeId, analyticsType = 'overview') {
    try {
      // Use the challenges API with analytics endpoint
      const response = await authenticatedAPI.get(this.apiName, `/challenges/${challengeId}/analytics`, {
        queryStringParameters: { type: analyticsType }
      });
      return response;
    } catch (error) {
      console.error('Error fetching challenge analytics:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async getChallengeLeaderboard(challengeId) {
    try {
      const response = await this.getChallengeAnalytics(challengeId, 'leaderboard');
      return response;
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async getPointsLeaderboard(challengeId) {
    try {
      // Use the points leaderboard endpoint for points_sabotage challenges
      const response = await authenticatedAPI.get(this.apiName, `/challenges/${challengeId}/leaderboard/points`);
      return response;
    } catch (error) {
      console.error('Error fetching points leaderboard:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async getDailyProgress(challengeId) {
    try {
      const response = await this.getChallengeAnalytics(challengeId, 'daily-progress');
      return response;
    } catch (error) {
      console.error('Error fetching daily progress:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async getTeamStats(challengeId) {
    try {
      const response = await this.getChallengeAnalytics(challengeId, 'team-stats');
      return response;
    } catch (error) {
      console.error('Error fetching team stats:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async createChallenge(challengeData) {
    try {
      // Map frontend format to backend format
      const apiData = {
        title: challengeData.name || challengeData.title,
        description: challengeData.description,
        challenge_type: challengeData.type || challengeData.challenge_type || 'individual',
        duration: challengeData.duration || 'custom',
        start_date: challengeData.startDate || challengeData.start_date,
        end_date: challengeData.endDate || challengeData.end_date,
        settings: {
          goal: challengeData.goal,
          category: challengeData.category || 'steps',
          isPublic: challengeData.isPublic !== false,
          ...challengeData.settings
        }
      };

      // Add optional fields
      if (challengeData.max_participants) apiData.max_participants = challengeData.max_participants;
      if (challengeData.max_teams) apiData.max_teams = challengeData.max_teams;
      if (challengeData.team_size_min) apiData.team_size_min = challengeData.team_size_min;
      if (challengeData.team_size_max) apiData.team_size_max = challengeData.team_size_max;

      const response = await authenticatedAPI.post(this.apiName, '/challenges', {
        body: apiData
      });
      
      return response.challenge || response.data || response;
    } catch (error) {
      console.error('Error creating challenge:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async updateChallenge(challengeId, challengeData) {
    try {
      const response = await authenticatedAPI.post(this.apiName, `/challenges/${challengeId}`, {
        body: challengeData
      });
      return response;
    } catch (error) {
      console.error('Error updating challenge:', error);
      throw error;
    }
  }

  async deleteChallenge(challengeId) {
    try {
      const response = await authenticatedAPI.del(this.apiName, `/challenges/${challengeId}`);
      return response;
    } catch (error) {
      console.error('Error deleting challenge:', error);
      throw error;
    }
  }

  async createTeam(challengeId, teamData) {
    try {
      const response = await authenticatedAPI.post(this.apiName, `/challenges/${challengeId}/create-team`, {
        body: teamData
      });
      return response;
    } catch (error) {
      console.error('Error creating team:', error);
      throw error;
    }
  }

  async joinTeam(challengeId, teamId) {
    try {
      const response = await authenticatedAPI.post(this.apiName, `/challenges/${challengeId}/join-team`, {
        body: { team_id: teamId }
      });
      return response;
    } catch (error) {
      console.error('Error joining team:', error);
      throw error;
    }
  }

  async getUserChallenges() {
    try {
      const response = await this.getChallenges({ my_challenges: true });
      return response;
    } catch (error) {
      console.error('Error fetching user challenges:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  async getAvailableChallenges() {
    try {
      const response = await this.getChallenges({ status: 'active' });
      return response;
    } catch (error) {
      console.error('Error fetching available challenges:', error);
      this.handleApiError(error);
      throw error;
    }
  }

  handleApiError(error) {
    if (error.response) {
      const { status, data } = error.response;
      switch (status) {
        case 400:
          throw new Error(data?.message || 'Invalid request data');
        case 401:
          throw new Error('Authentication failed. Please log in again.');
        case 403:
          throw new Error('Access denied. You may not have permission for this action.');
        case 404:
          throw new Error('Challenge not found');
        case 409:
          throw new Error(data?.message || 'Conflict - you may already be participating');
        case 429:
          throw new Error('Too many requests. Please try again later.');
        default:
          throw new Error(data?.error || data?.message || 'Challenge operation failed');
      }
    }
    throw error;
  }
}

const challengesApiService = new ChallengesApiService();
export default challengesApiService;